import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "../store";
import axiosInstance from "src/apis/axiosInstance";
import {
  APIResponseCover,
  Pagination,
  PartnerAPIResponseCover,
} from "./auth.d";
import { readAPIErrorMessage, throwErrApi } from "src/utils/errorUtil";
import { API_STATUS, DATA_LIMIT } from "src/utils/constants";

export interface InsuranceProviderDataForm {
  provider_name: string;
  provider_code: string;
  contact_person_name: string;
  contact_number: string;
  email_id: string;
  notes: string;
}

export interface InsuranceProviderEditDataForm extends InsuranceProviderDataForm {
  id: string;
}

export interface InsuranceProviderData {
  id: string;
  provider_name: string;
  provider_code: string;
  contact_person_name: string;
  contact_number: string;
  email_id: string;
  notes: string;
  status: number;
  created_date: string;
  updated_date: string;
}

export interface InsuranceProvidersState {
  data?: Pagination<InsuranceProviderData>;
  status: API_STATUS;
  list: {
    data: InsuranceProviderData[];
    status: API_STATUS;
  };
  view: {
    data?: InsuranceProviderData;
    status: API_STATUS;
  };
}

const initialState: InsuranceProvidersState = {
  data: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  list: {
    data: [],
    status: "idle",
  },
  view: {
    data: undefined,
    status: "idle",
  },
  status: "idle",
};

export const insuranceProvidersSlice = createSlice({
  name: "insuranceProviders",
  initialState,
  reducers: {
    refreshInsuranceProviderList: (state) => {
      state.status = "idle";
    },
    resetInsuranceProviderView(state) {
      state.view.status = "idle";
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchInsuranceProviders.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchInsuranceProviders.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
    });
    builder.addCase(fetchInsuranceProviders.rejected, (state) => {
      state.status = "error";
    });
    builder.addCase(fetchListInsuranceProviders.pending, (state) => {
      state.list.status = "pending";
    });
    builder.addCase(fetchListInsuranceProviders.fulfilled, (state, action) => {
      state.list.status = "success";
      state.list.data = action.payload;
    });
    builder.addCase(fetchListInsuranceProviders.rejected, (state) => {
      state.list.status = "error";
    });
    builder.addCase(addInsuranceProvider.fulfilled, (state) => {
      state.status = "idle";
    });
    builder.addCase(viewInsuranceProvider.fulfilled, (state, action) => {
      state.view.data = action.payload;
      state.view.status = "success";
    });
    builder.addCase(viewInsuranceProvider.pending, (state) => {
      state.view.status = "pending";
    });
    builder.addCase(viewInsuranceProvider.rejected, (state) => {
      state.view.status = "error";
    });
  },
});

export const fetchInsuranceProviders = createAsyncThunk<
  Pagination<InsuranceProviderData>,
  { page: number; keyword: string },
  { state: RootState }
>("insuranceProvider/fetch", async ({ page, keyword }, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      PartnerAPIResponseCover<Pagination<InsuranceProviderData>>,
      AxiosResponse<PartnerAPIResponseCover<Pagination<InsuranceProviderData>>>
    >(
      `partners/insurance_providers?limit=${DATA_LIMIT}&keyword=${keyword}&offset=${
        DATA_LIMIT * (page - 1)
      }&format=json`
    );
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const fetchListInsuranceProviders = createAsyncThunk<
  InsuranceProviderData[],
  void,
  { state: RootState }
>("insuranceProvider/fetchList", async (_, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      PartnerAPIResponseCover<InsuranceProviderData[]>,
      AxiosResponse<PartnerAPIResponseCover<InsuranceProviderData[]>>
    >(`partners/insurance_providers/search/?format=json`);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const deleteInsuranceProvider = createAsyncThunk<
  { status: string },
  string,
  { state: RootState }
>("insuranceProvider/delete", async (id, thunkAPI) => {
  try {
    await axiosInstance.delete<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>>
    >(`partners/insurance_providers/${id}?format=json`);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const updateInsuranceProvider = createAsyncThunk<
  { status: string },
  InsuranceProviderEditDataForm,
  { state: RootState }
>("insuranceProvider/update", async (data, thunkAPI) => {
  try {
    await axiosInstance.put<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>, InsuranceProviderEditDataForm>,
      InsuranceProviderEditDataForm
    >(`partners/insurance_providers/${data.id}?format=json`, { ...data });
    return thunkAPI.fulfillWithValue({ status: "pending" });
  } catch (e) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const viewInsuranceProvider = createAsyncThunk<
  InsuranceProviderData,
  string,
  { state: RootState }
>("insuranceProvider/view", async (id, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      APIResponseCover<InsuranceProviderData>,
      AxiosResponse<APIResponseCover<InsuranceProviderData>>
    >(`partners/insurance_providers/${id}?format=json`);
    return response.data.data;
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const addInsuranceProvider = createAsyncThunk<
  { status: string },
  InsuranceProviderDataForm,
  { state: RootState }
>("insuranceProvider/add", async (data, thunkAPI) => {
  try {
    await axiosInstance.post<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>, InsuranceProviderDataForm>,
      InsuranceProviderDataForm
    >("partners/insurance_providers?format=json", data);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const { refreshInsuranceProviderList, resetInsuranceProviderView } =
  insuranceProvidersSlice.actions;

export default insuranceProvidersSlice.reducer;
